import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import { autoUpdate, computePosition, offset, shift } from '@floating-ui/dom';
import { TranslateService } from '@ngx-translate/core';

import { ModifierKeysPressedService } from '@gfe/core/services/modifier-keys-pressed/modifier-keys-pressed.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import { isHexColor, isNil } from '@gfe/core/utils/shared-utils';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { BooleanCellComponent } from '@shared/components/table/table-cell/boolean/boolean-cell/boolean-cell.component';
import { DatetimeCellEditorComponent } from '@shared/components/table/table-cell/datetime/datetime-cell-editor/datetime-cell-editor.component';
import { DatetimeCellComponent } from '@shared/components/table/table-cell/datetime/datetime-cell/datetime-cell.component';
import { DurationCellComponent } from '@shared/components/table/table-cell/duration/duration-cell/duration-cell.component';
import { StringCellEditorComponent } from '@shared/components/table/table-cell/string/string-cell-editor/string-cell-editor.component';
import { UidCellEditorComponent } from '@shared/components/table/table-cell/uid/uid-cell-editor/uid-cell-editor.component';
import { WindowCellComponent } from '@shared/components/table/table-cell/window/window-cell/window-cell.component';
import { DisabledCellState } from '@shared/components/table/table.model';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';

import {
  ActionsOverlayComponent,
  CellOverlayAction
} from './actions-overlay/actions-overlay.component';
import { AddressCellComponent } from './address/address-cell/address-cell.component';
import { DeployStatusOverallCellComponent } from './deploy-status-overall/deploy-status-overall-cell.component';
import { EnumCellEditorComponent } from './enum/enum-cell-editor/enum-cell-editor.component';
import { EnumCellComponent } from './enum/enum-cell/enum-cell.component';
import { IdCellComponent } from './id/id-cell/id-cell.component';
import { JsonCellComponent } from './json/json-cell/json-cell.component';
import { KeyValueMapCellComponent } from './key-value-map/key-value-map-cell/key-value-map-cell.component';
import { NumberCellComponent } from './number/number-cell/number-cell.component';
import { PlanStatusCellComponent } from './plan-status/plan-status-cell/plan-status-cell.component';
import { StringWithBadgeCellComponent } from './string-with-badge/string-with-badge-cell.component';
import { StringCellComponent } from './string/string-cell/string-cell.component';
import { TableCellDataType } from './table-cell-data-type';
import { TagListCellComponent } from './tag-list/tag-list-cell/tag-list-cell.component';
import { TextListCellComponent } from './text-list/text-list-cell/text-list-cell.component';
import { UidLabelResolver } from './uid-label-resolver';
import { UidListCellComponent } from './uid-list/uid-list-cell/uid-list-cell.component';
import { UidCellComponent } from './uid/uid-cell/uid-cell.component';
import { isComplexCellValue, processTimeValue } from './utils';

export type { TableCellDataType } from './table-cell-data-type';

export interface CellWithOverlayActions {
  availableCellActions: CellOverlayAction[];
  availableCellShiftActions: CellOverlayAction[];
  executeAction?(action: CellOverlayAction): void;
  valueAsString?(value: unknown): string;
  /** Uid to add to the filter for the 'filter' overlay action; only meaningful for uid/uid-list cells. */
  filterValue?(): string | undefined;
}

export interface DataTypeConfig<T extends TableCellDataType = TableCellDataType> {
  type: T;
  enumValues?: string[];
  min?: number;
  max?: number;
  format?: string;
  placeholder?: string;
  validation?: (value: unknown) => string | null;
}

export interface CellEditEvent<T = unknown> {
  oldValue: T;
  newValue: T;
  rowIndex?: number;
  columnId?: string;
  triggerKey?: 'Enter' | 'Tab' | undefined; // undefined for blur/click outside
}

/**
 * Emitted whenever the user presses Enter or Tab while editing a cell, regardless
 * of whether the value changed (unlike `editCommit`). Drives Excel/Sheets-like
 * keyboard navigation between editable cells.
 */
export interface CellNavigationEvent {
  key: 'Enter' | 'Tab';
  shift: boolean;
}

@Component({
  selector: 'app-table-cell',
  standalone: true,
  imports: [
    StringCellComponent,
    StringWithBadgeCellComponent,
    IdCellComponent,
    NumberCellComponent,
    AddressCellComponent,
    ActionsOverlayComponent,
    PlanStatusCellComponent,
    DeployStatusOverallCellComponent,
    KeyValueMapCellComponent,
    UidCellComponent,
    UidListCellComponent,
    TagListCellComponent,
    TextListCellComponent,
    BooleanCellComponent,
    EnumCellComponent,
    EnumCellEditorComponent,
    JsonCellComponent,
    DurationCellComponent,
    DropdownComponent,
    DatetimeCellComponent,
    WindowCellComponent,
    DatetimeCellEditorComponent,
    StringCellEditorComponent,
    UidCellEditorComponent,
    TooltipComponent
  ],
  templateUrl: './table-cell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()'
  }
})
export class TableCellComponent<
  T extends TableCellDataType = TableCellDataType
> implements OnDestroy {
  private readonly modifierKeysService = inject(ModifierKeysPressedService);
  private readonly translate = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);

  dataType = input.required<TableCellDataType>();
  config = input<DataTypeConfig<T>>();

  allowEdit = input<boolean>(false);
  disabled = input<DisabledCellState | undefined>(undefined);
  bgColor = input<string | undefined>(undefined);
  errorForCell = input<boolean>(false);

  editorConfig = input<{
    uidEntityType?: string;
    uidEntityTypeVersion?: string;
    enumValues?: string[];
    enumLabels?: string[];
  }>();

  rowIndex = input<number>();
  columnId = input<string>();
  unit = input<string | undefined>(undefined);
  displayLabelFor = input<UidLabelResolver | undefined>(undefined);
  row = input<unknown>(undefined);
  targetEntityType = input<string | undefined>(undefined);
  filterable = input<boolean>(false);
  ownerEntityType = input<string>('');

  value = model<unknown>(null);

  editStart = output<void>();
  editCommit = output<CellEditEvent>();
  editNavigate = output<CellNavigationEvent>();
  editCancel = output<void>();
  /**
   * Every exit from edit mode, whatever closed it — unlike `editCommit`, which stays
   * silent when the value did not change, and `editCancel`, which covers only Escape.
   */
  editEnd = output<void>();
  validationError = output<string>();
  uidClick = output<unknown>();

  protected isEditing = signal<boolean>(false);

  protected isMouseOver = signal<boolean>(false);
  protected editValue: unknown = null;
  protected validationMessage = signal<string | null>(null);

  protected canEdit = computed(
    () => this.allowEdit() && (isNil(this.disabled()) || this.disabled()?.canStillEdit)
  );

  /**
   * In the default switch branch, route non-null object/array values to the JSON
   * cell so they don't stringify to "[object Object]". Primitives stay on the
   * string cell.
   */
  protected isComplexValue = computed<boolean>(() => isComplexCellValue(this.value()));

  private typedCellElement = viewChild<CellWithOverlayActions>('typedCellElement');
  private cellElement = viewChild<ElementRef<HTMLDivElement>>('cellElement');
  private editorElement = viewChild<ElementRef<HTMLDivElement>>('editorElement');
  private cleanupAutoUpdate?: () => void;

  private readonly isShiftPressed = this.modifierKeysService.isShiftPressed;
  protected readonly isMetaPressed = this.modifierKeysService.isMetaPressed;

  protected readonly onCloseHandler = () => this.commitEdit();

  protected getCellActions = computed<CellOverlayAction[] | undefined>(() => {
    const cell = this.typedCellElement();
    if (isNil(cell)) {
      return undefined;
    }
    if (isNil(this.value())) {
      return undefined;
    }
    return this.isShiftPressed()
      ? cell.availableCellShiftActions
      : cell.availableCellActions;
  });

  /**
   * Measured once when editing starts (see startEdit). Reading layout properties
   * (offsetWidth/offsetHeight) inside a computed would force a synchronous reflow
   * on every change-detection pass for every cell — the values are only consumed
   * by the edit overlay while a single cell is being edited.
   */
  protected cellWidth = signal<number>(0);
  protected cellHeight = signal<number>(0);

  /**
   * Pins the edit panel to the column width for reference pickers, whose body is
   * a fixed-size control: left to size itself it spills over the neighbouring
   * columns and hides which one is being filled in. Free-text editors stay
   * content-sized, since clipping a text field to a narrow column makes it
   * unusable.
   */
  protected editorWidth = computed<number>(() =>
    this.dataType() === 'uid' ? this.cellWidth() : 0
  );

  private handlers: Record<string, (cell: CellWithOverlayActions) => void> = {
    copy_raw: () => this.copyToClipboard(JSON.stringify(this.value())),
    copy: (cell) => {
      const valueToCopy = cell.valueAsString?.(this.value());
      if (valueToCopy) this.copyToClipboard(valueToCopy);
    },
    filter: (cell) => {
      const uid = cell.filterValue?.();
      if (uid) this.uidClick.emit(uid);
    }
  };

  constructor() {
    effect(() => {
      this.cleanupAutoUpdate?.();
      if (this.isEditing()) {
        const element = this.editorElement()?.nativeElement;
        const targetElement = this.cellElement()?.nativeElement;
        if (!isNil(targetElement) && !isNil(element)) {
          this.cleanupAutoUpdate = autoUpdate(targetElement, element, () => {
            computePosition(targetElement, element, {
              placement: 'bottom-start',
              middleware: [offset(-15), shift({ padding: 5 })]
            }).then(({ x, y }) => {
              Object.assign(element.style, {
                left: `${x - 5}px`,
                top: `${y}px`
              });
            });
          });
        }
      }
    });
  }

  protected onMouseEnter(): void {
    this.isMouseOver.set(true);
  }
  protected onMouseLeave(): void {
    this.isMouseOver.set(false);
  }

  executeAction(action: CellOverlayAction): void {
    const cell = this.typedCellElement();
    if (isNil(cell)) {
      return;
    }

    if (!isNil(cell.executeAction)) {
      cell.executeAction(action);
      return;
    }

    this.handlers[action]?.(cell);
  }

  private copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value).then(
      () => {
        this.notificationService.success(
          this.translate.instant('table.cell.overlay_actions.copy.message_success'),
          '',
          2000
        );
      },
      (err) => {
        console.error('Could not copy text: ', err);
        this.notificationService.error(
          this.translate.instant('table.cell.overlay_actions.copy.message_error'),
          ''
        );
      }
    );
  }

  private focus() {
    this.cellElement()?.nativeElement.focus();
  }

  private startEdit(): void {
    if (!this.canEdit() || this.isEditing()) return;

    // Measure the cell once, at the moment the edit overlay opens, so the
    // overlay/editor can size itself without reading layout on every CD pass.
    const el = this.cellElement()?.nativeElement;
    if (el) {
      this.cellWidth.set(el.offsetWidth);
      this.cellHeight.set(el.offsetHeight);
    }

    this.editValue = this.deepClone(this.value());
    this.isEditing.set(true);
    this.validationMessage.set(null);
    this.editStart.emit();
  }

  protected commitEdit(triggerKey?: 'Enter' | 'Tab'): void {
    if (!this.isEditing()) return;
    this.isEditing.set(false);
    // Announced before the unchanged-value return below, so closing an editor without
    // touching it still counts as leaving edit mode.
    this.editEnd.emit();

    const oldValue = this.value();
    const newValue = this.editValue;
    if (newValue === oldValue) {
      return;
    }

    const error = this.validate(newValue);
    if (error) {
      this.validationMessage.set(error);
      this.validationError.emit(error);
      return;
    }

    this.value.set(newValue);

    this.validationMessage.set(null);

    this.editCommit.emit({
      oldValue,
      newValue,
      rowIndex: this.rowIndex(),
      columnId: this.columnId(),
      triggerKey
    });
  }

  protected cancelEdit(): void {
    if (!this.isEditing()) return;

    this.isEditing.set(false);
    this.editValue = null;
    this.validationMessage.set(null);
    this.editCancel.emit();
    this.editEnd.emit();
  }

  protected updateEditValue(newValue: unknown): void {
    this.editValue = newValue;
  }

  protected onEnumValueChange(rawValue: string | null): void {
    this.updateEditValue(rawValue);
    this.commitEdit();
    // If the enum has labels, the model was just set to the raw value (e.g. "code:one")
    // by commitEdit, but the parent binding feeds rendered values (e.g. "one" via renderHook).
    // Angular will not re-push "one" if the binding expression hasn't changed, so we
    // proactively reset the model to the label value to keep them in sync.
    if (rawValue) {
      const editorCfg = this.editorConfig();
      const idx = editorCfg?.enumValues?.indexOf(rawValue) ?? -1;
      if (idx >= 0 && editorCfg?.enumLabels?.[idx] !== undefined) {
        this.value.set(editorCfg.enumLabels[idx]);
      }
    }
  }

  protected onCellClick(): void {
    if (this.canEdit() && !this.isEditing()) {
      this.startEdit();
    }
  }

  protected onEditorCellKeyDown(event: KeyboardEvent): void {
    // Prevent moving cell selection when editing a cell, i.e. arrows should
    // move the cursor within the text editor, and not move the cell selection
    const ignoredKeys: string[] = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'];
    if (ignoredKeys.includes(event.key)) {
      event.stopPropagation();
    }

    this.onCellKeyDown(event);
  }

  protected onCellKeyDown(event: KeyboardEvent): void {
    if (!this.isEditing()) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.startEdit();
      }
      if (event.key === 'Delete') {
        event.preventDefault();
        this.startEdit();
        this.editValue = null;
        this.commitEdit();
      }
    } else {
      if (event.key === 'Enter') {
        this.focus();
        event.preventDefault();
        this.commitEdit('Enter');
        this.editNavigate.emit({ key: 'Enter', shift: event.shiftKey });
      }
      if (event.key === 'Escape') {
        this.focus();
        event.preventDefault();
        this.cancelEdit();
      }
      if (event.key === 'Tab') {
        this.focus();
        // Prevent the browser's default tab focus move so navigation between
        // editable cells is fully controlled (Excel/Sheets-like).
        event.preventDefault();
        this.commitEdit('Tab');
        this.editNavigate.emit({ key: 'Tab', shift: event.shiftKey });
      }
    }
  }

  protected validate(value: unknown): string | null {
    const cfg = this.config();

    if (cfg?.validation) {
      const error = cfg.validation(value);
      if (error) return error;
    }

    const dataType = this.dataType();

    switch (dataType) {
      case 'number':
      case 'duration':
        if (typeof value !== 'number') return 'Must be a number';
        if (cfg?.min !== undefined && value < cfg.min) {
          return `Must be at least ${cfg.min}`;
        }
        if (cfg?.max !== undefined && value > cfg.max) {
          return `Must be at most ${cfg.max}`;
        }
        break;

      case 'enum': {
        const enumValues = cfg?.enumValues ?? this.editorConfig()?.enumValues;
        if (enumValues !== undefined && !enumValues.includes(value as string)) {
          return 'Invalid enum value';
        }
        break;
      }

      case 'color':
        if (!isHexColor(value)) {
          return 'Invalid color format';
        }
        break;

      // A time that cannot be read back is worse than no edit at all: it is written into
      // the row, saved to the owner's stored checkpoint, and read back on every later
      // visit. Refusing it here keeps the cell showing its previous value. Only `time`:
      // a `time-window` cell holds `{ start, duration }` or ISO bounds, which this reader
      // rejects by design and `processWindowValue` renders.
      case 'time':
        if (isNil(processTimeValue(value))) {
          return 'Invalid time';
        }
        break;
    }

    return null;
  }

  protected deepClone<T>(value: T): T {
    if (value === null || value === undefined) {
      return value;
    }

    try {
      return structuredClone(value);
    } catch (error) {
      console.warn(
        'TableCellComponent: Cannot clone value, using original reference:',
        error
      );
      return value;
    }
  }

  ngOnDestroy() {
    this.cleanupAutoUpdate?.();
  }

  protected getCellClasses() {
    const base =
      'group/cell relative flex h-full w-full cursor-pointer items-center px-2 py-1 transition-colors outline-none ring-inset overflow-hidden min-h-8';
    if (this.errorForCell()) {
      return `${base} bg-danger-soft group-hover:bg-danger/10 border border-danger`;
    }
    if (!isNil(this.disabled())) {
      return `${base} cursor-not-allowed bg-zinc-300 dark:bg-zinc-600 group-hover:bg-zinc-300 dark:group-hover:bg-zinc-600`;
    }

    return `${base} rounded-xs focus:bg-blue-400/20 focus:ring-2 focus:ring-blue-400`;
  }

  protected getCellBgStyle(): string | null {
    if (this.errorForCell() || !isNil(this.disabled())) {
      return null;
    }
    return this.bgColor() ?? null;
  }

  protected getCellTextColorStyle(): string | null {
    const bgColor = this.getCellBgStyle();
    if (isNil(bgColor)) return null;
    return this.getContrastTextColor(bgColor);
  }

  private getContrastTextColor(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return 'var(--color-zinc-900)';
    const toLinear = (c: number) => {
      const s = c / 255;
      return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const luminance =
      0.2126 * toLinear(parseInt(result[1], 16)) +
      0.7152 * toLinear(parseInt(result[2], 16)) +
      0.0722 * toLinear(parseInt(result[3], 16));
    return luminance > 0.179 ? 'var(--color-zinc-900)' : 'var(--color-zinc-50)';
  }
}
